/**
 * Resource Card Component
 * 
 * Beautiful card component for displaying resources in a grid.
 * Shows preview, title, description, category, and metrics.
 * 
 * @module ResourceCard
 */

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Video,
  Image as ImageIcon,
  Link as LinkIcon,
  Eye,
  Download,
  User,
  Star,
  Play,
  Edit3,
  Trash2,
  CheckCircle,
  Circle
} from 'lucide-react';
import { Resource, ResourceType, ResourceCategory } from '../../@types/resource';
import { ResourceService } from '../../services/ResourceService';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { stripHtml } from '../../utils/formatText';

/**
 * Props interface
 */
export interface ResourceCardProps {
  resource: Resource;
  onClick?: (resource: Resource) => void;
  showActions?: boolean;
  onEdit?: (resource: Resource) => void;
  onDelete?: (resource: Resource) => void;
  selectable?: boolean;
  selected?: boolean;
  onSelectToggle?: (resource: Resource) => void;
}

/**
 * Styled Components
 */
const Card = styled.div<{ clickable: boolean; selected: boolean }>`
  background: white;
  border-radius: ${BorderRadius.lg};
  overflow: hidden;
  box-shadow: ${Shadows.sm};
  border: ${props => (props.selected ? `2px solid ${ThemeColors.primary.main}` : `1px solid ${ThemeColors.border.light}`)};
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  /* Prevent Safari reading mode on mobile */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: manipulation;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${Shadows.lg};
    border-color: ${ThemeColors.primary.main};
  }
`;

const CardMedia = styled.div<{ type: ResourceType; hasImage: boolean }>`
  width: 100%;
  height: 200px;
  background: ${props =>
    props.hasImage
      ? ThemeColors.background.paper
      : (() => {
    switch (props.type) {
            case 'document':
              return `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`;
            case 'video':
              return `linear-gradient(135deg, #f093fb 0%, #f5576c 100%)`;
            case 'photo':
              return `linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)`;
            case 'link':
              return `linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)`;
            case 'text':
              return `linear-gradient(135deg, #fa709a 0%, #fee140 100%)`;
            default:
              return `linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark})`;
    }
        })()};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: ${props => (props.hasImage ? 'none' : "''")};
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.2) 0%, transparent 70%);
  }
`;

const MediaIcon = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  z-index: 1;

  svg {
    width: 40px;
    height: 40px;
  }
`;

const CardMediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: ${Spacing.md};
  right: ${Spacing.md};
  background: ${ThemeColors.warning.main};
  color: white;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 20px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  z-index: 2;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const CardContent = styled.div`
  padding: ${Spacing.lg};
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const SelectionBadge = styled.button<{ selected: boolean }>`
  position: absolute;
  top: -${Spacing.sm};
  right: -${Spacing.sm};
  width: 32px;
  height: 32px;
  border-radius: ${BorderRadius.round};
  border: none;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${Shadows.sm};

  svg {
    width: 20px;
    height: 20px;
    color: ${props => (props.selected ? ThemeColors.primary.main : ThemeColors.text.secondary)};
  }
`;

const CardHeader = styled.div`
  margin-bottom: ${Spacing.md};
`;

const CardTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.sm} 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CategoryBadge = styled.span<{ categoryId: ResourceCategory }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 16px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  background: ${props => {
    const category = ResourceService.getCategoryInfo(props.categoryId);
    return category ? `${category.color}20` : ThemeColors.background.cards;
  }};
  color: ${props => {
    const category = ResourceService.getCategoryInfo(props.categoryId);
    return category ? category.color : ThemeColors.text.secondary;
  }};
`;

const CardDescription = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  line-height: 1.5;
  margin: 0 0 ${Spacing.md} 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${Spacing.md};
  border-top: 1px solid ${ThemeColors.border.light};
  margin-top: auto;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.md};
  padding-top: ${Spacing.sm};
  border-top: 1px dashed ${ThemeColors.border.light};
`;

const ActionButton = styled.button<{ variant?: 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.md};
  border-radius: ${BorderRadius.md};
  border: none;
  cursor: pointer;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  background: ${props =>
    props.variant === 'danger' ? `${ThemeColors.error.light}` : `${ThemeColors.primary.light}`};
  color: ${props =>
    props.variant === 'danger' ? ThemeColors.error.dark : ThemeColors.primary.dark};
  transition: all 0.2s ease;

  &:hover {
    background: ${props =>
      props.variant === 'danger' ? `${ThemeColors.error.main}` : `${ThemeColors.primary.main}`};
    color: white;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const MetricsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
`;

const Metric = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.sm};
`;

const TagBadge = styled.span`
  padding: 2px ${Spacing.xs};
  background: ${ThemeColors.background.cards};
  color: ${ThemeColors.text.secondary};
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
`;

/**
 * Get resource type icon
 */
const getResourceIcon = (type: ResourceType) => {
  switch (type) {
    case 'document': return <FileText />;
    case 'video': return <Video />;
    case 'photo': return <ImageIcon />;
    case 'link': return <LinkIcon />;
    case 'text': return <FileText />;
    default: return <FileText />;
  }
};

/**
 * Format date
 */
/**
 * ResourceCard Component
 * 
 * @example
 * ```tsx
 * <ResourceCard
 *   resource={resource}
 *   onClick={handleViewResource}
 * />
 * ```
 */
export const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  onClick,
  showActions = false,
  onEdit,
  onDelete,
  selectable = false,
  selected = false,
  onSelectToggle
}) => {
  const { t } = useTranslation();
  const categoryInfo = ResourceService.getCategoryInfo(resource.category);
  const primaryImage =
    resource.thumbnailUrl ||
    resource.photoUrls?.[0] ||
    resource.files?.find(file => file.type?.startsWith('image/'))?.url ||
    null;

  const isSelectable = selectable && !!onSelectToggle;
  const isClickable = isSelectable || !!onClick;

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent event bubbling to parent elements
    e.stopPropagation();
    
    if (isSelectable) {
      onSelectToggle?.(resource);
    } else {
      onClick?.(resource);
    }
  };

  const handleSelectClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSelectToggle?.(resource);
  };

  return (
    <Card 
      onClick={handleCardClick} 
      clickable={isClickable} 
      selected={selected}
      onTouchStart={(e) => {
        // Prevent Safari reading mode on touch
        if (isClickable) {
          e.stopPropagation();
        }
      }}
    >
      <CardMedia type={resource.type} hasImage={!!primaryImage}>
        {resource.featured && (
          <FeaturedBadge>
            <Star />
            Featured
          </FeaturedBadge>
        )}
        {primaryImage ? (
          <>
            <CardMediaImage src={primaryImage} alt={resource.title} loading="lazy" />
            {resource.type === 'video' && (
              <MediaIcon>
                <Play />
              </MediaIcon>
            )}
          </>
        ) : (
        <MediaIcon>
          {resource.type === 'video' ? <Play /> : getResourceIcon(resource.type)}
        </MediaIcon>
        )}
      </CardMedia>

      <CardContent>
        {isSelectable && (
          <SelectionBadge
            type="button"
            selected={selected}
            aria-label={
              selected
                ? t('resources.unselectResource', 'Unselect resource')
                : t('resources.selectResource', 'Select resource')
            }
            onClick={handleSelectClick}
          >
            {selected ? <CheckCircle /> : <Circle />}
          </SelectionBadge>
        )}
        <CardHeader>
          <CategoryBadge categoryId={resource.category}>
            {categoryInfo?.icon} {categoryInfo?.name}
          </CategoryBadge>
          <CardTitle>{resource.title}</CardTitle>
        </CardHeader>

        <CardDescription>{stripHtml(resource.description)}</CardDescription>

        {resource.tags && resource.tags.length > 0 && (
          <TagsContainer>
            {resource.tags.slice(0, 3).map(tag => (
              <TagBadge key={tag}>{tag}</TagBadge>
            ))}
            {resource.tags.length > 3 && (
              <TagBadge>+{resource.tags.length - 3}</TagBadge>
            )}
          </TagsContainer>
        )}

        <CardFooter>
          <MetricsGroup>
            <Metric title="Views">
              <Eye />
              {resource.viewCount || 0}
            </Metric>
            <Metric title="Downloads">
              <Download />
              {resource.downloadCount || 0}
            </Metric>
          </MetricsGroup>
          <AuthorInfo title={`Created by ${resource.createdByName}`}>
            <User />
            {resource.createdByName}
          </AuthorInfo>
        </CardFooter>

        {showActions && !isSelectable && (
          <CardActions onClick={e => e.stopPropagation()}>
            {onEdit && (
              <ActionButton type="button" onClick={() => onEdit(resource)}>
                <Edit3 />
                {t('common.edit', 'Edit')}
              </ActionButton>
            )}
            {onDelete && (
              <ActionButton
                type="button"
                variant="danger"
                onClick={() => onDelete(resource)}
              >
                <Trash2 />
                {t('common.delete', 'Delete')}
              </ActionButton>
            )}
          </CardActions>
        )}
      </CardContent>
    </Card>
  );
};

